/* eslint-disable unicorn/no-null, no-continue, no-await-in-loop -- exptected for this feature */
import { ClockSkewError } from '../../errors/clockSkewError.js';
import { isErrorLike, wait } from '../../util.js';

import type { FetchEnhancer } from '../../types/fetchEnhancer.js';

// Node.js specific retry utils
export { isRetryAllowed, nodeRetryOn } from './nodeRetryOn.js';

export type RetryDelayFunction = (
  attempt: number,
  error: unknown,
  response: Response | null,
) => number;

export type RetryOnFunction = (
  attempt: number,
  error: unknown,
  response: Response | null,
) => boolean | Promise<boolean>;

export interface RetryOptions {
  /**
   *  Number of times to retry a request before failing.
   */
  retries?: number;
  /**
   * Delay between the retries in ms
   */
  retryDelay?: number | RetryDelayFunction;
  /**
   * A predicate function or list of response codes to control which responses should be retried. By default,
   * requests are retried when a server error is encountered (5xx)
   */
  retryOn?: RetryOnFunction;
}

export type RetryEnhancerOptions = {
  /**
   * Configuration options for the retry enhancer
   */
  retry?: Partial<RetryOptions>;
};

/**
 * Delay request exponentially on each retry
 */
export const exponentialDelay =
  (delayFactor = 100): RetryDelayFunction =>
  (attempt) => {
    const retryNumber = attempt + 1;
    const delay = 2 ** retryNumber * delayFactor;
    const randomSum = delay * 0.2 * Math.random(); // 0-20% of the delay
    return delay + randomSum;
  };

/**
 * @ignore
 */
export const DEFAULT_NUMBER_OF_RETRY = 3;

/**
 * @ignore
 */
export const DEFAULT_RETRY_OPTIONS = {
  retries: DEFAULT_NUMBER_OF_RETRY,
  retryDelay: exponentialDelay(),
  retryOn: (_attempt, error, response): boolean | Promise<boolean> => {
    if (error) {
      // will not retry on AbortError
      if (isErrorLike(error) && error.name === 'AbortError') return false;
      // will retry on network error
      return true;
    }
    // Retry on the server failed to fulfil an apparently valid request
    if (response && response.status >= 500 && response.status < 600) return true;
    return false;
  },
} satisfies RetryOptions;

const retryDelayFactory =
  (option: number | RetryDelayFunction) =>
  (attempt: number, error: unknown, response: Response | null): number => {
    if (typeof option === 'function') return option(attempt, error, response);
    return option;
  };

/**
 * Retry enhancer adds the ability to retry requests when they fail.
 */
export const retryEnhancer: FetchEnhancer<RetryEnhancerOptions> =
  (fetch) =>
  async (url, options): Promise<Response> => {
    const { retry: partialRetryOptions } = options ?? {};

    const retryOptions = {
      ...DEFAULT_RETRY_OPTIONS,
      ...partialRetryOptions,
    };

    const getRetryDelay = retryDelayFactory(retryOptions.retryDelay);

    for (const attempt of Array.from({ length: retryOptions.retries }).keys()) {
      try {
        const response = await fetch(url, options);

        if (await retryOptions.retryOn(attempt, null, response)) {
          await wait(getRetryDelay(attempt, null, response), options?.signal).catch(() => {});
          continue;
        }

        return response;
      } catch (error) {
        if (error instanceof ClockSkewError) {
          await wait(getRetryDelay(attempt, error, null), options?.signal).catch(() => {});
          continue;
        }
        if (await retryOptions.retryOn(attempt, error, null)) {
          await wait(getRetryDelay(attempt, error, null), options?.signal).catch(() => {});
          continue;
        }
        throw error;
      }
    }

    return fetch(url, options);
  };

/* eslint-enable unicorn/no-null, no-continue, no-await-in-loop -- exptected for this feature */
